import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from 'react';
import React from 'react';
import { MessageSquare, X, Send, Bot, User, Loader2, RotateCcw } from 'lucide-react';
import '../../User Side/styles/venue-chatbot.css';
import { API_BASE_URL } from '../config';

const normalizeMojibake = (value) => {
  const text = String(value ?? '');
  return text
    .replace(/â€¢/g, '•')
    .replace(/â€º/g, '>')
    .replace(/â€“/g, '-')
    .replace(/â€”/g, '-')
    .replace(/â€˜|â€™/g, "'")
    .replace(/â€œ|â€�/g, '"')
    .replace(/â‚±/g, 'PHP ');
};

const splitTableCells = (line) => {
  const trimmed = String(line ?? '').trim();
  const withoutOuterPipes = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return withoutOuterPipes.split('|').map((cell) => normalizeMojibake(cell).trim());
};

const isTableDividerRow = (line) => {
  if (!String(line ?? '').includes('|')) return false;
  const cells = splitTableCells(line).filter((cell) => cell.length > 0);
  if (cells.length < 2) return false;
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s+/g, '')));
};

const isLikelyTableRow = (line) => {
  if (!String(line ?? '').includes('|')) return false;
  const cells = splitTableCells(line);
  return cells.length > 1;
};

const normalizeHeaderKey = (header) => String(header ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const findCellByHeader = (headers, cells, aliases) => {
  for (let i = 0; i < headers.length; i += 1) {
    const key = headers[i];
    if (aliases.some((alias) => key.includes(alias))) {
      return String(cells[i] ?? '').trim();
    }
  }
  return '';
};

const tableRowsToListText = (headers, rows) => {
  const normalizedHeaders = headers.map(normalizeHeaderKey);
  const resultLines = [];

  rows.forEach((cells, idx) => {
    const cleanedCells = cells.map((cell) => String(cell ?? '').replace(/\s+/g, ' ').trim());
    if (cleanedCells.every((cell) => !cell)) return;

    const rowNumber = findCellByHeader(normalizedHeaders, cleanedCells, ['#', ' no ', ' number ']) || String(idx + 1);
    const venueName = findCellByHeader(normalizedHeaders, cleanedCells, ['venue', 'name']) || cleanedCells[0] || `Venue ${rowNumber}`;
    const venueType = findCellByHeader(normalizedHeaders, cleanedCells, ['type']);
    const capacity = findCellByHeader(normalizedHeaders, cleanedCells, ['capacity', 'pax']);
    const location = findCellByHeader(normalizedHeaders, cleanedCells, ['location']);
    const packagePricing = findCellByHeader(normalizedHeaders, cleanedCells, ['package', 'pricing', 'price']);
    const promo = findCellByHeader(normalizedHeaders, cleanedCells, ['promo', 'discount']);

    const headerParts = [];
    if (venueType) headerParts.push(venueType);
    if (capacity) headerParts.push(`up to ${capacity} pax`);

    resultLines.push(`${rowNumber}) ${venueName}${headerParts.length ? ` (${headerParts.join(', ')})` : ''}`);
    if (location) resultLines.push(`- Location: ${location}`);
    if (packagePricing) resultLines.push(`- Packages: ${packagePricing}`);
    if (promo && !/^n\/?a$|^none$/i.test(promo)) resultLines.push(`- Promo: ${promo}`);
    resultLines.push('');
  });

  return resultLines.join('\n').trim();
};

const convertTableBlocksToListText = (inputText) => {
  const text = normalizeMojibake(inputText);
  const lines = text.split('\n');
  const output = [];

  let index = 0;
  while (index < lines.length) {
    const current = lines[index];
    const next = lines[index + 1] ?? '';

    if (isLikelyTableRow(current) && isTableDividerRow(next)) {
      const headers = splitTableCells(current);
      const rows = [];
      index += 2;

      while (index < lines.length) {
        const rowLine = lines[index];
        if (!rowLine.trim()) break;
        if (!rowLine.includes('|')) break;
        if (isTableDividerRow(rowLine)) {
          index += 1;
          continue;
        }

        const rowCells = splitTableCells(rowLine);
        if (rowCells.length <= 1) break;
        rows.push(rowCells);
        index += 1;
      }

      if (rows.length > 0) {
        output.push(tableRowsToListText(headers, rows));
      }

      if ((lines[index] ?? '').trim() === '') {
        output.push('');
        index += 1;
      }

      continue;
    }

    output.push(current);
    index += 1;
  }

  return output.join('\n');
};

const parseVenueHeader = (line) => {
  const trimmed = String(line ?? '').trim();
  const numberedMatch = trimmed.match(/^(\d+)\s*[.)]\s*(.+)$/) || trimmed.match(/^(\d+)\s*\|\s*(.+)$/);
  if (!numberedMatch) return null;

  const rank = numberedMatch[1];
  const body = String(numberedMatch[2] ?? '').trim();
  if (!body) return null;

  if (body.includes('|')) {
    const cells = body.split('|').map((cell) => normalizeMojibake(cell).trim()).filter(Boolean);
    const [name = `Venue ${rank}`, type = '', capacity = '', packages = '', promo = ''] = cells;
    const metaParts = [];
    if (type) metaParts.push(type);
    if (capacity) metaParts.push(`up to ${capacity} pax`);

    const seededLines = [];
    if (packages) seededLines.push(`Packages: ${packages}`);
    if (promo) seededLines.push(`Promo: ${promo}`);

    return {
      rank,
      name,
      meta: metaParts.join(', '),
      seededLines,
    };
  }

  const plainMatch = body.match(/^(.+?)(?:\s+\((.+)\))?$/);
  return {
    rank,
    name: String(plainMatch?.[1] ?? body).trim(),
    meta: String(plainMatch?.[2] ?? '').trim(),
    seededLines: [],
  };
};

const extractDurationPricesFromText = (text) => {
  const normalized = String(text ?? '').replace(/\s+/g, ' ').trim();
  const prices = {};

  const phpPattern = /\b(6|12|24)\s*(?:h|hr|hrs|hour|hours)\s*[:-]?\s*(PHP\s*[0-9][0-9,.\s]*(?:\([^)]*\))?)/gi;
  let match = phpPattern.exec(normalized);
  while (match) {
    prices[`${match[1]}h`] = String(match[2] ?? '').replace(/\s+/g, ' ').trim();
    match = phpPattern.exec(normalized);
  }

  if (Object.keys(prices).length > 0) return prices;

  const numericPattern = /\b(6|12|24)\s*(?:h|hr|hrs|hour|hours)\s*[:-]?\s*([0-9][0-9,.]*)/gi;
  let numericMatch = numericPattern.exec(normalized);
  while (numericMatch) {
    prices[`${numericMatch[1]}h`] = `PHP ${numericMatch[2]}`;
    numericMatch = numericPattern.exec(normalized);
  }

  return prices;
};

const mergePrices = (target, incoming) => {
  Object.entries(incoming).forEach(([key, value]) => {
    if (!target[key] && value) {
      target[key] = value;
    }
  });
};

const parseVenueCardsFromText = (text) => {
  const lines = String(text ?? '').split('\n').map((line) => normalizeMojibake(line));
  const intro = [];
  const cards = [];
  const outro = [];

  let index = 0;
  while (index < lines.length) {
    const current = lines[index].trim();

    if (!current) {
      index += 1;
      continue;
    }

    const header = parseVenueHeader(current);
    if (!header) {
      if (cards.length === 0) {
        intro.push(current);
      } else {
        outro.push(current);
      }
      index += 1;
      continue;
    }

    const card = {
      rank: header.rank,
      name: header.name,
      meta: header.meta,
      location: '',
      prices: {},
      promo: '',
      notes: [...header.seededLines],
    };

    index += 1;
    while (index < lines.length) {
      const detailRaw = lines[index];
      const detail = detailRaw.trim();

      if (!detail) {
        const next = String(lines[index + 1] ?? '').trim();
        if (parseVenueHeader(next)) {
          index += 1;
          break;
        }
        index += 1;
        continue;
      }

      if (parseVenueHeader(detail)) {
        break;
      }

      const clean = detail.replace(/^[-*•]\s+/, '').trim();
      if (!clean) {
        index += 1;
        continue;
      }

      const locationMatch = clean.match(/^location\s*:\s*(.+)$/i);
      if (locationMatch) {
        card.location = String(locationMatch[1] ?? '').trim();
        index += 1;
        continue;
      }

      const promoMatch = clean.match(/^(promo|discount)\s*:\s*(.+)$/i);
      if (promoMatch) {
        card.promo = String(promoMatch[2] ?? '').trim();
        index += 1;
        continue;
      }

      const directDurationMatch = clean.match(/^(6|12|24)\s*(?:h|hr|hrs|hour|hours)\s*[:-]?\s*(.+)$/i);
      if (directDurationMatch) {
        card.prices[`${directDurationMatch[1]}h`] = String(directDurationMatch[2] ?? '').trim();
        index += 1;
        continue;
      }

      mergePrices(card.prices, extractDurationPricesFromText(clean));
      card.notes.push(clean);
      index += 1;
    }

    const signalScore = [
      card.location,
      card.promo,
      Object.keys(card.prices).length > 0 ? 'prices' : '',
      card.notes.find((line) => /php|package|promo|discount|capacity|location|type/i.test(line)) || '',
    ].filter(Boolean).length;

    if (signalScore > 0) {
      cards.push(card);
    } else {
      const fallbackLines = [`${card.rank}) ${card.name}${card.meta ? ` (${card.meta})` : ''}`, ...card.notes];
      if (cards.length === 0) {
        intro.push(...fallbackLines);
      } else {
        outro.push(...fallbackLines);
      }
    }
  }

  if (cards.length === 0) {
    return null;
  }

  return { intro, cards, outro };
};

const renderVenueCardMessage = (parsed) => {
  const elements = [];

  parsed.intro.forEach((line, i) => {
    elements.push(
      React.createElement('p', { key: `intro-${i}`, className: 'venue-chatbot__text-line' }, formatBotLine(line))
    );
  });

  elements.push(
    React.createElement(
      'div',
      { key: 'venue-cards', className: 'venue-chatbot__venue-cards' },
      parsed.cards.map((card) => React.createElement(
        'article',
        { key: `card-${card.rank}-${card.name}`, className: 'venue-chatbot__venue-card' },
        React.createElement(
          'div',
          { className: 'venue-chatbot__venue-card-head' },
          React.createElement('span', { className: 'venue-chatbot__venue-rank' }, card.rank),
          React.createElement(
            'div',
            { className: 'venue-chatbot__venue-title-wrap' },
            React.createElement('p', { className: 'venue-chatbot__venue-name' }, card.name),
            card.meta ? React.createElement('p', { className: 'venue-chatbot__venue-meta' }, card.meta) : null
          )
        ),
        card.location ? React.createElement(
          'p',
          { className: 'venue-chatbot__venue-line' },
          React.createElement('span', { className: 'venue-chatbot__venue-label' }, 'Location'),
          React.createElement('span', null, card.location)
        ) : null,
        Object.keys(card.prices).length > 0 ? React.createElement(
          'div',
          { className: 'venue-chatbot__venue-prices' },
          ['6h', '12h', '24h'].map((slot) => card.prices[slot]
            ? React.createElement(
              'div',
              { key: `${card.rank}-${slot}`, className: 'venue-chatbot__price-chip' },
              React.createElement('span', { className: 'venue-chatbot__price-chip-label' }, slot),
              React.createElement('span', { className: 'venue-chatbot__price-chip-value' }, card.prices[slot])
            )
            : null)
        ) : null,
        card.promo && !/^n\/?a$|^none$/i.test(card.promo)
          ? React.createElement('p', { className: 'venue-chatbot__venue-promo' }, `Promo: ${card.promo}`)
          : null,
        card.notes.length > 0
          ? React.createElement(
            'ul',
            { className: 'venue-chatbot__venue-notes' },
            card.notes
              .filter((line) => !/^location\s*:/i.test(line))
              .filter((line) => !/^(promo|discount)\s*:/i.test(line))
              .slice(0, 3)
              .map((line, idx) => React.createElement('li', { key: `${card.rank}-note-${idx}` }, formatBotLine(line)))
          )
          : null
      ))
    )
  );

  parsed.outro.forEach((line, i) => {
    elements.push(
      React.createElement('p', { key: `outro-${i}`, className: 'venue-chatbot__text-line' }, formatBotLine(line))
    );
  });

  return elements;
};

const stripBulletPrefix = (line) => String(line ?? '').replace(/^[-*•]\s+/, '').trim();

const parseAvailabilityCardFromText = (text) => {
  const lines = String(text ?? '').split('\n').map((line) => normalizeMojibake(line));
  const intro = [];
  const notes = [];
  const slots = {};

  const card = {
    venue: '',
    date: '',
    nextDate: '',
    occupied: '',
  };

  let hasAvailabilitySignal = false;
  let signalScore = 0;

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const clean = stripBulletPrefix(trimmed);

    const availabilityHeader = clean.match(/^availability(?:\s+check)?(?:\s+for)?\s+(.+?)\s+(?:on|for)\s+(\d{4}-\d{2}-\d{2})/i);
    if (availabilityHeader) {
      if (!card.venue) card.venue = String(availabilityHeader[1] ?? '').trim();
      if (!card.date) card.date = String(availabilityHeader[2] ?? '').trim();
      hasAvailabilitySignal = true;
      signalScore += 2;
      return;
    }

    if (/\b(availability|available|occupied slots?|time slots?|supported durations?)\b/i.test(clean)) {
      hasAvailabilitySignal = true;
      signalScore += 1;
    }

    const venueMatch = clean.match(/^venue\s*:\s*(.+)$/i);
    if (venueMatch) {
      card.venue = String(venueMatch[1] ?? '').trim();
      signalScore += 1;
      return;
    }

    const dateMatch = clean.match(/^date\s*:\s*(\d{4}-\d{2}-\d{2})/i);
    if (dateMatch) {
      card.date = String(dateMatch[1] ?? '').trim();
      signalScore += 1;
      return;
    }

    const nextDateMatch = clean.match(/^next\s*date\s*:\s*(\d{4}-\d{2}-\d{2})/i);
    if (nextDateMatch) {
      card.nextDate = String(nextDateMatch[1] ?? '').trim();
      signalScore += 1;
      return;
    }

    const occupiedMatch = clean.match(/^(occupied(?:\s*slots?)?|booked(?:\s*slots?)?)\s*:\s*(.+)$/i);
    if (occupiedMatch) {
      card.occupied = String(occupiedMatch[2] ?? '').trim();
      signalScore += 1;
      return;
    }

    const durationMatch = clean.match(/^(?:available\s*)?(6|12|24)\s*(?:h|hr|hrs|hour|hours)(?:\s*(?:slots?|times?|start(?:\s*time)?s?))?\s*:\s*(.+)$/i);
    if (durationMatch) {
      slots[`${durationMatch[1]}h`] = String(durationMatch[2] ?? '').trim();
      signalScore += 2;
      return;
    }

    if (hasAvailabilitySignal) {
      notes.push(clean);
    } else {
      intro.push(clean);
    }
  });

  const slotCount = Object.keys(slots).length;
  const hasCoreData = Boolean(card.venue || card.date || card.occupied || slotCount > 0 || card.nextDate);
  if (!hasAvailabilitySignal || !hasCoreData || signalScore < 3) {
    return null;
  }

  return {
    intro,
    card,
    slots,
    notes: notes.filter((line) => !/^availability\s*:/i.test(line)),
  };
};

const parseBookingCardFromText = (text) => {
  const lines = String(text ?? '').split('\n').map((line) => normalizeMojibake(line));
  const intro = [];
  const notes = [];

  const card = {
    venue: '',
    date: '',
    duration: '',
    start: '',
    end: '',
    endDayHint: '',
    promo: '',
    referenceId: '',
    status: '',
    mode: 'summary',
  };

  let score = 0;
  let bookingSignal = false;

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const clean = stripBulletPrefix(trimmed);

    if (/^please\s+confirm\s+your\s+booking\s+details/i.test(clean)) {
      card.mode = 'confirmation';
      bookingSignal = true;
      score += 3;
      return;
    }

    if (/\b(booking\s+details|booking\s+request|booking\s+status|reference\s*(id|number)|pending\s+approval)\b/i.test(clean)) {
      bookingSignal = true;
      score += 1;
    }

    const venueMatch = clean.match(/^venue\s*:\s*(.+)$/i);
    if (venueMatch) {
      card.venue = String(venueMatch[1] ?? '').trim();
      score += 1;
      return;
    }

    const dateMatch = clean.match(/^date\s*:\s*(\d{4}-\d{2}-\d{2})/i);
    if (dateMatch) {
      card.date = String(dateMatch[1] ?? '').trim();
      score += 1;
      return;
    }

    const durationMatch = clean.match(/^duration\s*:\s*(.+)$/i);
    if (durationMatch) {
      card.duration = String(durationMatch[1] ?? '').trim();
      score += 1;
      return;
    }

    const startMatch = clean.match(/^start\s*:\s*(.+)$/i);
    if (startMatch) {
      card.start = String(startMatch[1] ?? '').trim();
      score += 1;
      return;
    }

    const endMatch = clean.match(/^end\s*:\s*(.+)$/i);
    if (endMatch) {
      const endText = String(endMatch[1] ?? '').trim();
      const split = endText.match(/^(.+?)\s*\((same day|next day)\)$/i);
      card.end = String(split?.[1] ?? endText).trim();
      card.endDayHint = String(split?.[2] ?? '').trim();
      score += 1;
      return;
    }

    const promoMatch = clean.match(/^(promo|discount)\s*:\s*(.+)$/i);
    if (promoMatch) {
      card.promo = String(promoMatch[2] ?? '').trim();
      score += 1;
      return;
    }

    const refMatch = clean.match(/reference\s*(?:id|number)\s*[:#-]?\s*([A-Z]{1,4}-?[A-Z0-9-]+)/i)
      || clean.match(/\b(VB-[A-Z0-9-]+)\b/i);
    if (refMatch) {
      card.referenceId = String(refMatch[1] ?? '').trim();
      card.mode = 'submitted';
      score += 3;
      bookingSignal = true;
      return;
    }

    if (/pending\s+approval/i.test(clean)) {
      card.status = 'Pending approval';
      score += 2;
      return;
    }

    if (bookingSignal) {
      notes.push(clean);
    } else {
      intro.push(clean);
    }
  });

  const fieldCount = [card.venue, card.date, card.duration, card.start, card.end, card.referenceId].filter(Boolean).length;
  const hasBookingData = card.referenceId || fieldCount >= 3;
  if (!hasBookingData || score < 4) {
    return null;
  }

  return {
    intro,
    card,
    notes,
  };
};

const renderLabeledLine = (key, label, value, extra) => {
  if (!value) return null;
  return React.createElement(
    'p',
    { key, className: 'venue-chatbot__structured-line' },
    React.createElement('span', { className: 'venue-chatbot__structured-label' }, `${label}:`),
    React.createElement('span', { className: 'venue-chatbot__structured-value' }, `${value}${extra ? ` (${extra})` : ''}`)
  );
};

const renderAvailabilityCardMessage = (parsed) => {
  const elements = [];

  parsed.intro.forEach((line, i) => {
    elements.push(
      React.createElement('p', { key: `availability-intro-${i}`, className: 'venue-chatbot__text-line' }, formatBotLine(line))
    );
  });

  const slotOrder = ['6h', '12h', '24h'];
  elements.push(
    React.createElement(
      'article',
      { key: 'availability-card', className: 'venue-chatbot__availability-card' },
      React.createElement(
        'div',
        { className: 'venue-chatbot__structured-card-head' },
        React.createElement('p', { className: 'venue-chatbot__structured-card-title' }, 'Availability Check'),
        (parsed.card.venue || parsed.card.date)
          ? React.createElement(
            'p',
            { className: 'venue-chatbot__structured-card-subtitle' },
            [parsed.card.venue, parsed.card.date].filter(Boolean).join(' | ')
          )
          : null
      ),
      React.createElement(
        'div',
        { className: 'venue-chatbot__structured-fields' },
        renderLabeledLine('availability-occupied', 'Occupied', parsed.card.occupied || 'None'),
        renderLabeledLine('availability-next-date', 'Next date', parsed.card.nextDate)
      ),
      Object.keys(parsed.slots).length > 0
        ? React.createElement(
          'div',
          { className: 'venue-chatbot__availability-slots' },
          slotOrder.map((slot) => parsed.slots[slot]
            ? React.createElement(
              'p',
              { key: `availability-slot-${slot}`, className: 'venue-chatbot__structured-line' },
              React.createElement('span', { className: 'venue-chatbot__structured-label' }, `${slot}:`),
              React.createElement('span', { className: 'venue-chatbot__structured-value' }, parsed.slots[slot])
            )
            : null)
        )
        : null,
      parsed.notes.length > 0
        ? React.createElement(
          'ul',
          { className: 'venue-chatbot__structured-notes' },
          parsed.notes.slice(0, 4).map((line, idx) => React.createElement('li', { key: `availability-note-${idx}` }, formatBotLine(line)))
        )
        : null
    )
  );

  return elements;
};

const renderBookingCardMessage = (parsed) => {
  const elements = [];

  parsed.intro.forEach((line, i) => {
    elements.push(
      React.createElement('p', { key: `booking-intro-${i}`, className: 'venue-chatbot__text-line' }, formatBotLine(line))
    );
  });

  const title = parsed.card.referenceId
    ? 'Booking Request Submitted'
    : parsed.card.mode === 'confirmation'
      ? 'Booking Confirmation'
      : 'Booking Details';

  elements.push(
    React.createElement(
      'article',
      { key: 'booking-card', className: 'venue-chatbot__booking-card' },
      React.createElement(
        'div',
        { className: 'venue-chatbot__structured-card-head' },
        React.createElement('p', { className: 'venue-chatbot__structured-card-title' }, title),
        parsed.card.referenceId
          ? React.createElement('span', { className: 'venue-chatbot__booking-ref' }, `Ref: ${parsed.card.referenceId}`)
          : null
      ),
      React.createElement(
        'div',
        { className: 'venue-chatbot__structured-fields' },
        renderLabeledLine('booking-venue', 'Venue', parsed.card.venue),
        renderLabeledLine('booking-date', 'Date', parsed.card.date),
        renderLabeledLine('booking-duration', 'Duration', parsed.card.duration),
        renderLabeledLine('booking-start', 'Start', parsed.card.start),
        renderLabeledLine('booking-end', 'End', parsed.card.end, parsed.card.endDayHint),
        renderLabeledLine('booking-promo', 'Promo', parsed.card.promo)
      ),
      parsed.card.status
        ? React.createElement('p', { className: 'venue-chatbot__booking-status' }, parsed.card.status)
        : null,
      parsed.notes.length > 0
        ? React.createElement(
          'ul',
          { className: 'venue-chatbot__structured-notes' },
          parsed.notes.slice(0, 4).map((line, idx) => React.createElement('li', { key: `booking-note-${idx}` }, formatBotLine(line)))
        )
        : null
    )
  );

  return elements;
};

const formatBotLine = (text) => {
  const parts = [];
  let remaining = normalizeMojibake(text);
  let keyIdx = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const before = remaining.slice(0, boldMatch.index);
      if (before) parts.push(before);
      parts.push(React.createElement('strong', { key: `b${keyIdx++}` }, boldMatch[1]));
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
    } else {
      parts.push(remaining);
      break;
    }
  }
  return parts;
};

const renderBotMessage = (text) => {
  const normalizedText = convertTableBlocksToListText(text);
  const parsedBookingCard = parseBookingCardFromText(normalizedText);
  if (parsedBookingCard) {
    return renderBookingCardMessage(parsedBookingCard);
  }

  const parsedAvailabilityCard = parseAvailabilityCardFromText(normalizedText);
  if (parsedAvailabilityCard) {
    return renderAvailabilityCardMessage(parsedAvailabilityCard);
  }

  const parsedVenueCards = parseVenueCardsFromText(normalizedText);
  if (parsedVenueCards) {
    return renderVenueCardMessage(parsedVenueCards);
  }

  const lines = normalizedText.split('\n');
  const elements = [];
  let listItems = [];
  let subItems = [];
  let inList = false;

  const flushSubItems = () => {
    if (subItems.length > 0) {
      listItems.push(
        React.createElement('ul', { key: `sub-${elements.length}-${listItems.length}`, className: 'venue-chatbot__sub-list' }, subItems)
      );
      subItems = [];
    }
  };

  const flushList = () => {
    flushSubItems();
    if (listItems.length > 0) {
      elements.push(
        React.createElement('ul', { key: `list-${elements.length}`, className: 'venue-chatbot__list' }, listItems)
      );
      listItems = [];
    }
    inList = false;
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    if (!trimmed) {
      if (inList) flushList();
      elements.push(React.createElement('div', { key: `gap-${i}`, className: 'venue-chatbot__line-gap' }));
      return;
    }

    const subMatch = trimmed.match(/^[+-]\s+(.*)/);
    if (subMatch) {
      subItems.push(
        React.createElement('li', { key: `si-${i}` }, formatBotLine(subMatch[1]))
      );
      return;
    }

    const bulletMatch = trimmed.match(/^[•*]\s+(.*)/);
    if (bulletMatch) {
      flushSubItems();
      inList = true;
      listItems.push(
        React.createElement('li', { key: `li-${i}` }, formatBotLine(bulletMatch[1]))
      );
      return;
    }

    const numMatch = trimmed.match(/^\d+[.)]\s+(.*)/);
    if (numMatch) {
      flushSubItems();
      inList = true;
      listItems.push(
        React.createElement('li', { key: `li-${i}` }, formatBotLine(numMatch[1]))
      );
      return;
    }

    flushList();
    elements.push(
      React.createElement('p', { key: `p-${i}`, className: 'venue-chatbot__text-line' }, formatBotLine(trimmed))
    );
  });

  flushList();
  return elements;
};

export default function VenueChatbot({ user, onBookingCreated }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: 'Hello! I\'m your venue booking assistant. I can help you:\n\n• Browse available venues\n• Check venue details and availability\n• Book using 6, 12, or 24-hour packages\n• Handle bookings that can end the next day\n\nHow can I help you today?',
      actions: ['Browse available venues', 'Check venue details and availability', 'Book using a 6/12/24-hour package'],
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async (directMessage) => {
    const trimmed = (typeof directMessage === 'string' ? directMessage : input).trim();
    if (!trimmed || sending) return;

    const userMessage = { role: 'user', text: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    if (typeof directMessage !== 'string') setInput('');
    setSending(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          userId: user?.id,
          userName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
          userEmail: user?.email || '',
          userPhone: user?.phone || '',
          sessionId,
        }),
      });

      const data = await response.json();

      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: normalizeMojibake(data.reply || 'I apologize, I could not process that. Please try again.') },
      ]);

      if (data.bookingCreated && onBookingCreated) {
        onBookingCreated();
      }
    } catch (err) {
      console.error('Chatbot error:', err);
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReset = () => {
    setMessages([
      {
        role: 'bot',
        text: 'Conversation reset. I can help with package-based bookings (6/12/24 hours), including overnight schedules.',
        actions: ['Browse available venues', 'Check venue details and availability', 'Book using a 6/12/24-hour package'],
      },
    ]);
  };

  const handleQuickAction = (actionText) => {
    if (sending) return;
    setInput('');
    handleSend(actionText);
  };

  return (
    <React.Fragment>
      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          type="button"
          className="venue-chatbot__toggle"
          onClick={() => setIsOpen(true)}
          aria-label="Open venue booking assistant"
        >
          <MessageSquare size={24} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="venue-chatbot">
          {/* Header */}
          <div className="venue-chatbot__header">
            <div className="venue-chatbot__header-info">
              <div className="venue-chatbot__header-icon">
                <Bot size={20} />
              </div>
              <div>
                <h3>Venue Booking Assistant</h3>
                <span className="venue-chatbot__status">Online</span>
              </div>
            </div>
            <div className="venue-chatbot__header-actions">
              <button
                type="button"
                className="venue-chatbot__header-btn"
                onClick={handleReset}
                title="Reset conversation"
              >
                <RotateCcw size={16} />
              </button>
              <button
                type="button"
                className="venue-chatbot__header-btn"
                onClick={() => setIsOpen(false)}
                title="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="venue-chatbot__messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`venue-chatbot__message venue-chatbot__message--${msg.role}`}
              >
                <div className="venue-chatbot__message-avatar">
                  {msg.role === 'bot' ? <Bot size={16} /> : <User size={16} />}
                </div>
                <div className="venue-chatbot__message-content">
                  <div className="venue-chatbot__message-bubble">
                    {msg.role === 'bot' ? renderBotMessage(msg.text) : msg.text}
                  </div>
                  {msg.actions && msg.actions.length > 0 && index === messages.length - 1 && !sending && (
                    <div className="venue-chatbot__quick-actions">
                      {msg.actions.map((action, actionIdx) => (
                        <button
                          key={actionIdx}
                          type="button"
                          className="venue-chatbot__quick-action-btn"
                          onClick={() => handleQuickAction(action)}
                        >
                          {action}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="venue-chatbot__message venue-chatbot__message--bot">
                <div className="venue-chatbot__message-avatar">
                  <Bot size={16} />
                </div>
                <div className="venue-chatbot__message-bubble venue-chatbot__message-typing">
                  <Loader2 size={16} className="venue-chatbot__spinner" />
                  <span>Thinking...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="venue-chatbot__input-area">
            <input
              ref={inputRef}
              type="text"
              className="venue-chatbot__input"
              placeholder="Ask about venues or book one..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
            />
            <button
              type="button"
              className="venue-chatbot__send-btn"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              aria-label="Send message"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

VenueChatbot.propTypes = {
  user: PropTypes.object,
  onBookingCreated: PropTypes.func,
};
